import type { ResponseCookie } from 'next/dist/compiled/@edge-runtime/cookies'
import type { NextRequest, NextResponse } from 'next/server'

import { ONE_YEAR_IN_SECONDS } from '@src/utils/constants.util'

export function getBrowserCookie(name: string) {
  if (typeof document === 'undefined') return undefined
  const cookie = document.cookie.split('; ').find((cookie) => cookie.startsWith(`${name}=`))
  return cookie ? decodeURIComponent(cookie.split('=')[1]) : undefined
}

export function setBrowserCookie(name: string, value: string, options?: string) {
  if (typeof document === 'undefined') return
  const defaultOptions = `path=/; max-age=${ONE_YEAR_IN_SECONDS}`
  document.cookie = `${name}=${value}; ${options ?? defaultOptions};`
}

export function removeBrowserCookie(name: string) {
  if (typeof document === 'undefined') return
  document.cookie = `${name}=; path=/; max-age=0;`
}

export function resetCookie(name: string, options?: string) {
  if (typeof document === 'undefined') return
  const decodedCookieValue = getBrowserCookie(name)
  if (!decodedCookieValue) return
  document.cookie = `${name}=${decodedCookieValue}; ${options ?? ''};`
}

function serializeCookieOptions(options: Partial<ResponseCookie>): string {
  return Object.entries(options ?? {})
    .map(([key, value]) => `${key}=${value}`)
    .join('; ')
}

/**
 * Isomorphic version of Next.js cookies API.
 * @see https://nextjs.org/docs/app/api-reference/functions/cookies
 */
export function isomorphicCookies(args?: { request?: NextRequest; response?: NextResponse }) {
  const isBrowser = typeof window !== 'undefined'
  const isEdgeRuntime = typeof EdgeRuntime !== 'undefined'
  return {
    get: async (name: string) => {
      if (isBrowser) {
        const cookie = document.cookie.split('; ').find((cookie) => cookie.startsWith(`${name}=`))
        return cookie ? decodeURIComponent(cookie.split('=')[1]) : undefined
      } else if (isEdgeRuntime) {
        if (!args?.request)
          throw new Error('[isomorphicCookies] request is required in Edge Runtime')
        return args?.request?.cookies.get(name)?.value
      } else {
        const { cookies } = await import('next/headers')
        const cookieStore = await cookies()
        return cookieStore.get(name)?.value
      }
    },
    set: async (name: string, value: string, options?: Partial<ResponseCookie>) => {
      if (isBrowser) {
        const defaultOptions: Partial<ResponseCookie> = { path: '/', maxAge: ONE_YEAR_IN_SECONDS }
        document.cookie = `${name}=${value}; ${serializeCookieOptions({ ...defaultOptions, ...options })};`
      } else if (isEdgeRuntime) {
        if (!args?.response)
          throw new Error('[isomorphicCookies] response is required in Edge Runtime')
        args?.response?.cookies.set(name, value, options)
      } else {
        const { cookies } = await import('next/headers')
        const cookieStore = await cookies()
        cookieStore.set(name, value, options)
      }
    },
    delete: async (name: string) => {
      if (isBrowser) {
        document.cookie = `${name}=; path=/; max-age=0;`
      } else if (isEdgeRuntime) {
        if (!args?.response)
          throw new Error('[isomorphicCookies] response is required in Edge Runtime')
        args?.response?.cookies.delete(name)
      } else {
        const { cookies } = await import('next/headers')
        const cookieStore = await cookies()
        cookieStore.delete(name)
      }
    },
    has: async (name: string) => {
      if (isBrowser) {
        const cookie = document.cookie.split('; ').find((cookie) => cookie.startsWith(`${name}=`))
        return !!cookie
      } else if (isEdgeRuntime) {
        if (!args?.request)
          throw new Error('[isomorphicCookies] request is required in Edge Runtime')
        return args?.request?.cookies.has(name)
      } else {
        const { cookies } = await import('next/headers')
        const cookieStore = await cookies()
        return cookieStore.has(name)
      }
    },
  }
}
